/**
 * A* pathfinding for Word Drift
 * Uses embeddings-based cosine similarity as heuristic when goal word is provided
 * Falls back to BFS (heuristic = 0) when no goal word is provided
 */

import { logger } from '@mental-mint/shared-utils';
import { PriorityQueue } from './PriorityQueue';
import type { DictionaryEntry } from './DictionaryTypes';
import type { EmbeddingsManager } from './EmbeddingsManager';
import { getAllNeighbors } from './NeighborExtractor';

export interface PathResult {
  path: string[];      // Full path from current to goal (includes both endpoints)
  distance: number;    // Total cost of path
  found: boolean;      // Whether a valid path was found
  nodesExplored: number; // Number of nodes explored (for performance monitoring)
}

/**
 * Calculate cosine similarity between two words using embeddings
 * Returns value in range [0, 1] or null if embeddings not available
 */
function calculateCosineSimilarity(
  word1: string,
  word2: string,
  embeddingsManager: EmbeddingsManager
): number | null {
  if (!word1 || !word2) return null;
  
  const vec1 = embeddingsManager.getVector(word1);
  const vec2 = embeddingsManager.getVector(word2);
  
  if (!vec1 || !vec2) return null;
  
  // Calculate cosine similarity using dot product
  let dotProduct = 0;
  let norm1 = 0;
  let norm2 = 0;
  
  for (let i = 0; i < vec1.length; i++) {
    dotProduct += vec1[i] * vec2[i];
    norm1 += vec1[i] * vec1[i];
    norm2 += vec2[i] * vec2[i];
  }
  
  if (norm1 === 0 || norm2 === 0) return null;
  
  return dotProduct / (Math.sqrt(norm1) * Math.sqrt(norm2));
}

/**
 * Calculate heuristic (estimated remaining cost) from word to goal
 * Uses 1 - cosineSimilarity as an admissible heuristic
 * Returns 0 if no goal word provided (becomes BFS)
 */
function heuristic(word: string, goal: string | undefined, embeddingsManager: EmbeddingsManager): number {
  if (!goal) return 0; // No goal = BFS mode
  
  const similarity = calculateCosineSimilarity(word, goal, embeddingsManager);
  if (similarity === null) return 0.5; // Assume moderate distance if unknown
  return 1.0 - similarity;
}

/**
 * Calculate edge cost between two words
 * Uses 1 - cosineSimilarity if embeddings available, otherwise 1.0
 */
function calculateEdgeCost(word1: string, word2: string, embeddingsManager: EmbeddingsManager): number {
  const similarity = calculateCosineSimilarity(word1, word2, embeddingsManager);
  if (similarity === null) return 1.0; // Default cost for semantic/definition edges
  return 1.0 - similarity;
}

/**
 * Reconstruct path from came-from map
 */
function reconstructPath(cameFrom: Map<string, string>, current: string): string[] {
  const path = [current];
  while (cameFrom.has(current)) {
    current = cameFrom.get(current)!;
    path.unshift(current);
  }
  return path;
}

/**
 * Find path from current word to goal word using A* search
 * 
 * @param currentWord - Starting word
 * @param goalWord - Target word (optional - if not provided, acts as BFS)
 * @param dictionary - Dictionary with entries and relations
 * @param embeddingsManager - Embeddings manager for similarity calculations
 * @param options - Optional configuration
 * @returns PathResult with path, distance, and metadata
 */
export function findPath(
  currentWord: string,
  goalWord: string | undefined,
  dictionary: Record<string, DictionaryEntry>,
  embeddingsManager: EmbeddingsManager,
  options: {
    maxDepth?: number;  // Maximum search depth (default: 20)
  } = {}
): PathResult {
  const maxDepth = options.maxDepth ?? 20;
  
  // Check if embeddings are loaded
  if (!embeddingsManager.loaded) {
    throw new Error('[Pathfinding] Embeddings not loaded - cannot perform pathfinding');
  }
  
  const startTime = performance.now();
  const currentLower = currentWord.toLowerCase();
  const goalLower = goalWord?.toLowerCase();
  
  logger.debug(`[Pathfinding] Finding path: "${currentWord}" → "${goalWord || 'unknown'}" (max depth: ${maxDepth})`);
  
  // Check if words have dictionary entries
  if (!dictionary[currentLower]) {
    logger.debug(`[Pathfinding] Current word "${currentWord}" not in dictionary`);
    return { path: [], distance: 0, found: false, nodesExplored: 0 };
  }
  
  if (goalLower && !dictionary[goalLower]) {
    logger.debug(`[Pathfinding] Goal word "${goalWord}" not in dictionary`);
    return { path: [], distance: 0, found: false, nodesExplored: 0 };
  }
  
  // Initialize A* data structures
  const openSet = new PriorityQueue<string>();
  const closedSet = new Set<string>();
  const gScores = new Map<string, number>();
  const cameFrom = new Map<string, string>();
  
  // Start with current word
  const startGScore = 0;
  const startHScore = heuristic(currentLower, goalLower, embeddingsManager);
  gScores.set(currentLower, startGScore);
  openSet.insert(currentLower, startGScore + startHScore);
  
  let nodesExplored = 0;
  
  // A* main loop
  while (!openSet.isEmpty()) {
    const current = openSet.extractMin();
    if (!current) break;
    
    nodesExplored++;
    
    // Check if we've reached the goal
    if (goalLower && current === goalLower) {
      const path = reconstructPath(cameFrom, current);
      const duration = (performance.now() - startTime).toFixed(2);
      logger.debug(`[Pathfinding] ✅ Path found in ${duration}ms: ${path.join(' → ')} (${nodesExplored} nodes explored)`);
      return {
        path,
        distance: gScores.get(current) || 0,
        found: true,
        nodesExplored
      };
    }
    
    // Check depth limit
    const currentDepth = reconstructPath(cameFrom, current).length - 1;
    if (currentDepth >= maxDepth) {
      continue; // Skip expanding this node
    }
    
    closedSet.add(current);
    
    // Explore neighbors
    const neighbors = getAllNeighbors(current, dictionary, goalLower, embeddingsManager);
    
    for (const neighborWord of neighbors) {
      const neighbor = neighborWord.variations.onClickDestination; // Use correct graph node
      if (closedSet.has(neighbor)) continue;
      
      // Calculate tentative g-score
      const edgeCost = calculateEdgeCost(current, neighbor, embeddingsManager);
      const currentGScore = gScores.get(current) || 0;
      const tentativeGScore = currentGScore + edgeCost;
      
      // If this is a better path to neighbor, update
      const neighborGScore = gScores.get(neighbor);
      if (neighborGScore === undefined || tentativeGScore < neighborGScore) {
        cameFrom.set(neighbor, current);
        gScores.set(neighbor, tentativeGScore);
        
        const hScore = heuristic(neighbor, goalLower, embeddingsManager);
        const fScore = tentativeGScore + hScore;
        
        openSet.insert(neighbor, fScore);
      }
    }
  }
  
  // No path found
  const duration = (performance.now() - startTime).toFixed(2);
  logger.debug(`[Pathfinding] ❌ No path found in ${duration}ms (${nodesExplored} nodes explored, max depth: ${maxDepth})`);
  return { path: [], distance: 0, found: false, nodesExplored };
}

/**
 * Find shortest path length only (convenience wrapper)
 */
export function findPathLength(
  currentWord: string,
  goalWord: string | undefined,
  dictionary: Record<string, DictionaryEntry>,
  embeddingsManager: EmbeddingsManager,
  maxDepth: number = 20
): number | null {
  const result = findPath(currentWord, goalWord, dictionary, embeddingsManager, { maxDepth });
  return result.found ? result.distance : null;
}
